import {Logger, type ILogObj} from 'tslog';
import {getLocalStorageValue, isFirefox} from '@netx/core-utils';

const defaultLogLevel = process.env.NODE_ENV === 'development' ? 0 : 3;
const preferredLogLevel = getLocalStorageValue('logLevel');

let logLevel: number = defaultLogLevel;
if (preferredLogLevel != null) {
	const _logLevel = parseInt(preferredLogLevel, 10);
	if (!isNaN(_logLevel)) {
		logLevel = Math.max(0, Math.min(5, _logLevel));
	}
}

/**
 * Default logger instance
 *
 * @example
 * logger.info('foo');
 */
export const logger = new Logger<ILogObj>({
	hideLogPositionForProduction: true,
	maskValuesOfKeysCaseInsensitive: true,
	minLevel: logLevel,
	type: 'pretty',
	// https://github.com/fullstack-build/tslog/issues/262
	// https://bugzilla.mozilla.org/show_bug.cgi?id=1720116
	stylePrettyLogs: !isFirefox(),
});

const separator = '>';

/**
 * Get a prefixed logger
 * @param prefix What to prefix any logging with
 * @returns A logger with the provided prefix
 *
 * @example
 * const logger = getPrefixedLogger('foo bar');
 * logger.info('baz'); // 'foo bar > baz'
 */
export const getPrefixedLogger = (prefix: string) => {
	return {
		debug: (...args: any[]) =>
			logger.debug.apply(logger, [prefix, separator, ...args]),
		error: (...args: any[]) =>
			logger.error.apply(logger, [prefix, separator, ...args]),
		fatal: (...args: any[]) =>
			logger.fatal.apply(logger, [prefix, separator, ...args]),
		info: (...args: any[]) =>
			logger.info.apply(logger, [prefix, separator, ...args]),
		silly: (...args: any[]) =>
			logger.silly.apply(logger, [prefix, separator, ...args]),
		trace: (...args: any[]) =>
			logger.trace.apply(logger, [prefix, separator, ...args]),
		warn: (...args: any[]) =>
			logger.warn.apply(logger, [prefix, separator, ...args]),
	};
};
